<template>
  <DashboardLayout :breadcrumbs="breadcrumbs">
    <div class="container is-fluid">
      <div class="level">
        <div class="level-left">
          <h1 class="title is-5">{{ $t('issueTracker.list') }}</h1>
        </div>
        <div class="level-right">
          <div class="field is-grouped controls">
            <div class="control">
              <button @click="goTo('createIssueTracker')"
                      class="button is-success">
                {{ $t('issueTracker.newIssue') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="level table-actions">
        <div class="level-left">
          <form @submit.prevent="onSearch">
            <div class="field is-grouped controls">
              <div class="control">
                <b-input v-model="q" :placeholder="$t('interface.search')"></b-input>
              </div>
              <div class="control mr-10">
                <button type="submit" class="button is-primary">
                  <b-icon icon="magnify" size="is-small"></b-icon>
                </button>
              </div>
            </div>
          </form>
        </div>
        <div class="level-right">
          <div class="control mt-5">
            <a @click="showFilters = !showFilters">
              <b-icon icon="filter-outline" size="is-small"></b-icon>
              {{ $t('interface.advancedSearch') }}
              <b-icon icon="chevron-down" size="is-small"></b-icon>
            </a>
          </div>
        </div>
      </div>

      <div v-if="showFilters" class="mt-15">
        <form @submit.prevent="loadData">
          <div class="columns is-multiline">
            <div class="column is-2">
              <b-field>
                <form-select :label="$t('issues.type')" v-model="filters.list_mode">
                  <option value="none">{{ $t('interface.all') }}</option>
                  <option value="maintained">{{ $t('issueTracker.incoming') }}</option>
                  <option value="executed">{{ $t('issueTracker.outgoing') }}</option>
                </form-select>
              </b-field>
            </div>
            <div class="column is-2">
              <b-field>
                <form-select :label="$t('issues.term')" v-model="filters.expiration">
                  <option value="none">{{ $t('interface.all') }}</option>
                  <option value="expired">{{ $t('issueTracker.expired') }}</option>
                  <option value="expiring">{{ $t('issueTracker.expiring') }}</option>
                </form-select>
              </b-field>
            </div>
            <div class="column is-2">
              <b-field>
                <form-select :label="$t('issues.state')" v-model="filters.state">
                  <option value="none">{{ $t('interface.all') }}</option>
                  <option value="new">{{ $t('workflow.states.new') }}</option>
                  <option value="in_process">{{ $t('workflow.states.in_process') }}</option>
                  <option value="done">{{ $t('workflow.states.done') }}</option>
                </form-select>
              </b-field>
            </div>
            <div class="column is-2">
              <b-field>
                <form-select :label="$t('issues.priority')" v-model="filters.priority">
                  <option value="none">{{ $t('interface.all') }}</option>
                  <option value="low">{{ $t('workflow.priorities.low') }}</option>
                  <option value="medium">{{ $t('workflow.priorities.medium') }}</option>
                  <option value="high">{{ $t('workflow.priorities.high') }}</option>
                </form-select>
              </b-field>
            </div>
            <div class="column is-4">
              <b-field>
                <form-select :label="$t('issues.category')" v-model="filters.category">
                  <option value="none">{{ $t('interface.all') }}</option>
                  <option v-for="category in categories"
                          :key="category.id"
                          :value="category.id">
                    {{ category.name }}
                  </option>
                </form-select>
              </b-field>
            </div>
            <div class="column is-12">
              <div class="field is-grouped">
                <div class="control">
                  <button type="submit" class="button is-primary">{{ $t('interface.apply') }}</button>
                </div>
                <div class="control">
                  <button type="button" class="button is-danger" @click="resetFilters">
                    <b-icon icon="filter-remove-outline" size="is-small"></b-icon>
                    <span>{{ $t('interface.clearFilters') }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <b-table class="mt-10"
               :data="data"
               :loading="loading"

               paginated
               backend-pagination
               :total="total"
               :per-page="perPage"
               @page-change="onPageChange"

               backend-sorting
               default-sort-direction="desc"
               :default-sort="[sortField, sortOrder]"
               @sort="onSort">
        <template slot-scope="props">
          <b-table-column :label="$t('issues.category')" field="category" sortable>
            <a @click.prevent="detail(props.row.id)">
              <span :class="`priority ${props.row.priority} mr-10`"></span>
              <span class="unread-indicator" v-if="!props.row.is_viewed"></span>
              <span>{{ props.row.category__name }}</span>
              <b-tooltip :label="props.row.is_author ? $t('issueTracker.outgoing') : $t('issueTracker.incoming')"
                         type="is-light">
                <b-icon :icon="`arrow-${props.row.is_author ? 'up' : 'down'}`"
                        size="is-small"
                        class="has-text-grey-light mr-0 ml-5">
                </b-icon>
              </b-tooltip>
            </a>
          </b-table-column>
          <b-table-column :label="$t('issues.state')" field="state" sortable>
            {{ $t(`workflow.states.${props.row.state}`) }}
          </b-table-column>
          <b-table-column :label="$t('issues.term')" width="180" field="term_date" sortable>
            <template v-if="props.row.term">
              <span :class="{expired: props.row.is_expired}">
                {{ props.row.term | timeFormat }}
                {{ props.row.term | localedDateWithYear }}
              </span>
            </template>
          </b-table-column>
          <b-table-column :label="$t('issues.author')" width="180">
            {{ props.row.author__initials }}
          </b-table-column>
          <b-table-column :label="$t('issues.executor')" width="180">
            {{ props.row.executor__initials ? props.row.executor__initials : $t('issueTracker.notAssigned') }}
          </b-table-column>
        </template>

        <template slot="empty">
          <section class="section">
            <div class="content is-fluid has-text-grey has-text-centered">
              <p>
                {{ $t('interface.nothingFound') }}
                <a class="is-dashed" @click.prevent="resetFilters">{{ $t('interface.resetFiltersAndSearch') }}</a>.
              </p>
            </div>
          </section>
        </template>
      </b-table>
    </div>
  </DashboardLayout>
</template>

<script>
  import {mapGetters, mapState} from 'vuex'
  import {priorityColors, stateColors} from '@/colorTypes'
  import DashboardLayout from '@/components/DashboardLayout'
  import GovernmentAgencySelect from '@/components/GovernmentAgencySelect'
  import {deleteObjectKeysByValue} from '@/utils'

  const defaultFilters = {
    list_mode: 'none',
    category: 'none',
    state: 'none',
    priority: 'none',
    participation: 'none',
    expiration: 'none'
  }

  export default {
    components: {
      DashboardLayout,
      GovernmentAgencySelect
    },
    data() {
      return {
        data: [],
        loading: false,
        total: 0,
        page: 1,
        perPage: 15,
        q: '',
        sortField: '',
        sortOrder: 'desc',
        priorityColors,
        stateColors,
        showFilters: false,
        filters: {...defaultFilters}
      }
    },
    methods: {
      loadData() {
        this.loading = true
        this.checkedRows = []
        this.$axios.get('issue_tracker/issues/', {
          params: {
            page: this.page,
            page_size: this.perPage,
            q: this.q,
            ordering: `${this.sortOrder === 'desc' ? '-' : ''}${this.sortField}`,
            ...deleteObjectKeysByValue(this.filters, 'none')
          }
        }).then((response) => {
          this.loading = false
          this.total = response.data.count
          this.data = response.data.results
        }).catch((error) => {
          console.warn('ERROR:', error)
        })
      },
      onSort(field, order) {
        this.sortField = field
        this.sortOrder = order
        this.loadData()
      },
      onPageChange(page) {
        this.page = page
        this.loadData()
      },
      onSearch() {
        this.loadData()
      },
      resetFilters() {
        this.q = ''
        this.filters = {...defaultFilters}
        this.showFilters = false
        this.loadData()
      },
      goTo(route) {
        this.$router.push({name: route})
      },
      detail(id) {
        this.$router.push({
          name: 'issueDetail',
          params: {issueId: id}
        })
      }
    },
    computed: {
      ...mapState('IssueTracker', ['categories']),
      checkedRowsCount() {
        return this.checkedRows.length
      },
      breadcrumbs() {
        return [
          {name: 'issues'}
        ]
      }
    },
    mounted() {
      this.loadData()
      this.$store.dispatch('IssueTracker/getRelations')
    }
  }
</script>
